/**
 * Helpers de formatação para exibição WooCommerce no ecommerce interno.
 * @module wooFormatters
 */

import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import type { Patient } from "fhir/r5";
import type { WooAddress } from "src-next/modules/patient-supporting-documents/marketplace/types";

/** Extrai e-mail do Patient FHIR (telecom system=email). */
export function getPatientEmail(patient: Patient): string {
  return (
    patient.telecom?.find((telecom) => telecom.system === "email")?.value?.trim() ||
    ""
  );
}

/** Telefone do Patient FHIR. */
export function getPatientPhone(patient: Patient): string {
  return (
    patient.telecom?.find((telecom) => telecom.system === "phone")?.value ||
    patient.telecom?.[0]?.value ||
    ""
  );
}

/** CPF (identifier TAX) do Patient FHIR. */
export function getPatientCpf(patient: Patient): string {
  const tax = patient.identifier?.find((id) =>
    id.type?.coding?.some((coding) => coding.code === "TAX"),
  );
  return tax?.value || patient.identifier?.[0]?.value || "";
}

/** Nome de exibição do Patient FHIR. */
export function getPatientDisplayName(patient: Patient): string {
  const official = patient.name?.find((name) => name.use === "official");
  const first = patient.name?.[0];
  return (
    official?.text ||
    first?.text ||
    [...(first?.given || []), first?.family].filter(Boolean).join(" ") ||
    "Não informado"
  );
}

/** Formata endereço WC em linhas legíveis; vazio se não houver dados úteis. */
export function formatWooAddress(address?: WooAddress): string[] {
  if (!address) return [];

  const name = [address.first_name, address.last_name].filter(Boolean).join(" ");
  const street = [address.address_1, address.address_2].filter(Boolean).join(", ");
  const cityLine = [address.city, address.state, address.postcode]
    .filter(Boolean)
    .join(" — ");

  return [
    name,
    street,
    cityLine,
    address.country,
    address.email,
    address.phone,
  ].filter((line): line is string => Boolean(line?.trim()));
}

/** Total com moeda (ex.: BRL 129.90). */
export function formatWooMoney(total?: string, currency?: string): string {
  if (!total) return "—";
  return currency ? `${currency} ${total}` : total;
}

/** Data ISO WC → pt-BR local. */
export function formatWooDate(iso?: string): string {
  if (!iso) return "—";
  const date = dayjs(iso);
  if (!date.isValid()) return iso;
  return date.locale("pt-br").format("DD/MM/YYYY, HH:mm:ss");
}

/** Rótulo amigável do status do pedido. */
export function formatWooStatus(status: string): string {
  const labels: Record<string, string> = {
    pending: "Pendente",
    processing: "Processando",
    "on-hold": "Em espera",
    completed: "Concluído",
    cancelled: "Cancelado",
    refunded: "Reembolsado",
    failed: "Falhou",
  };
  return labels[status] || status;
}
